/**
 * PWA manifest update notifier.
 *
 * Detects when the computed per-host manifest (name / short_name / icons /
 * theme / start_url) has changed since the last visit and surfaces a toast
 * telling the user to refresh — or, if already installed to home screen,
 * reinstall to pick up the new label.
 *
 * Pure presentation, no business logic. Runs once on app load.
 */

import { toast } from "sonner";
import { deriveLabel, ROOT_HOSTS, MANIFEST_VERSION } from "./dynamicManifest";

const STORAGE_KEY = "ptc:manifest-fingerprint:v1";
const BASE_MANIFEST_URL = `/manifest-v5.webmanifest?v=${MANIFEST_VERSION}`;

async function sha256(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  // iOS Safari + Android Chrome
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    // @ts-expect-error iOS-only
    window.navigator.standalone === true
  );
}

export async function checkManifestUpdate(): Promise<void> {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!("crypto" in window) || !crypto.subtle) return;

  try {
    const host = window.location.hostname.toLowerCase();
    const label = deriveLabel(host);

    // Build a fingerprint of what the user *would* see for this host.
    const res = await fetch(BASE_MANIFEST_URL, { cache: "no-cache" });
    if (!res.ok) return;
    const base = await res.json();
    const computed = label
      ? { ...base, name: label.name, short_name: label.shortName }
      : base;

    const fingerprintInput = JSON.stringify({
      host,
      name: computed.name,
      short_name: computed.short_name,
      start_url: computed.start_url,
      theme_color: computed.theme_color,
      background_color: computed.background_color,
      icons: computed.icons,
    });
    const fingerprint = await sha256(fingerprintInput);

    const storageKey = `${STORAGE_KEY}:${host}`;
    const previous = localStorage.getItem(storageKey);

    if (previous && previous !== fingerprint) {
      const installed = isStandalone();
      const title = installed
        ? "App update available"
        : "Site update available";
      const description = installed
        ? "Reinstall this app to your home screen to update the icon and name."
        : "Refresh the page to load the latest version.";

      toast(title, {
        description,
        duration: 12000,
        action: installed
          ? undefined
          : {
              label: "Refresh",
              onClick: () => window.location.reload(),
            },
      });
    }

    localStorage.setItem(storageKey, fingerprint);
  } catch {
    /* non-fatal — notifier is best-effort */
  }
}

// Silence unused warning for ROOT_HOSTS re-export hint
void ROOT_HOSTS;
